import React, { useState, useEffect, useCallback } from 'react';
import { X, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const DesenRevizeGecmisiModal = ({ isOpen, onClose, desenId, desenNo, isPublic = false }) => {
  const [revizeler, setRevizeler] = useState([]);
  const [onayDurumu, setOnayDurumu] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadRevizeler = useCallback(async () => {
    setLoading(true);
    try {
      const { getRevizelerPublic, getRevizeler } = await import('../../services/desenService');
      const getRevizelerFn = isPublic ? getRevizelerPublic : getRevizeler;
      
      const response = await getRevizelerFn(desenId);
      if (response.success) {
        setRevizeler(response.data.revizeler || []);
        setOnayDurumu(response.data.onay_durumu);
      }
    } catch (error) {
      console.error('Revizeler yüklenemedi:', error);
      toast.error('Revizeler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [desenId, isPublic]);

  useEffect(() => {
    if (isOpen && desenId) {
      loadRevizeler();
    }
  }, [isOpen, desenId, loadRevizeler]);

  if (!isOpen) return null;

  const getDurumBadge = (durum) => {
    switch (durum) {
      case 'onay_bekliyor':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
            Onay Bekliyor
          </span>
        );
      case 'revize':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
            Revize
          </span>
        );
      case 'onayli':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
            Onaylı
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Revize Geçmişi
              </h2>
              {desenNo && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Desen: {desenNo}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {onayDurumu && getDurumBadge(onayDurumu)}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-gray-400 dark:text-gray-500 animate-spin" />
            </div>
          ) : revizeler.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Henüz revize bulunmuyor</p>
            </div>
          ) : (
            <div className="space-y-4">
              {revizeler.map((revize, index) => (
                <div
                  key={revize.id || index}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Revize #{revizeler.length - index}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(revize.revize_tarihi)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {revize.kritik_metni}
                      </p>
                    </div>
                    {revize.onay_tarihi && (
                      <div className="flex flex-col items-end gap-1">
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Onaylandı
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(revize.onay_tarihi)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesenRevizeGecmisiModal;
